import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Activite } from '../../../../../../common/src/entities';
import ActivitePDF from './ActivitePDF';
import api from '../../../../Utils/api';
import { PDFDocument, rgb } from 'pdf-lib';
import { useRecoilState } from 'recoil';
import { etatMasquerCovid19 } from '../../../../State';
import { BiFile } from 'react-icons/bi';

const pageNumberOffset = 50;
const pageFooter = 10;

async function merge(pdfsToMerge: Blob[]): Promise<Blob> {
    const mergedPdf = await PDFDocument.create();
    for (const pdfBytes of pdfsToMerge) {
        if (pdfBytes.type === 'application/pdf') {
            const pdf = await PDFDocument.load(await pdfBytes.arrayBuffer());
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => {
                const pageNumber = (mergedPdf.getPageCount() + 1).toString();
                page.drawText(pageNumber, {
                    x: page.getWidth() - pageNumberOffset,
                    y: page.getHeight() / pageFooter - pageNumberOffset,
                    size: 12,
                    color: rgb(0, 0, 0),
                });
                mergedPdf.addPage(page);
            });
        }
    }
    const bytes = await mergedPdf.save();
    return new Blob([bytes], { type: 'application/pdf' });
}

export function useLoadPdf(activite: Activite, masquerCovid: boolean) {
    const [loading, setLoading] = useState(true);
    const [blob, setBlob] = useState<Blob | undefined>();
    const [pj, setPj] = useState<Blob | undefined>();
    const [data, setData] = useState<Blob | undefined>();
    const id_pj = activite?.fk_id_fichier_annexe;
    //reload le pdf quand l'activite change
    useEffect(() => {
        let isMounted = true; // Pour contrôler l'état après le démontage
        setBlob(undefined);
        if (activite) {
            const instance = pdf(<ActivitePDF activite={activite} masquerCovid={masquerCovid} />);
            instance.toBlob().then((result) => {
                if (isMounted) {
                    setBlob(result);
                    instance.container.finish();
                }
            });
        }
        return () => {
            isMounted = false;
        };
    }, [activite, masquerCovid]);
    //reload la piece jointe quand elle change
    useEffect(() => {
        let isMounted = true; // Pour contrôler l'état après le démontage
        setPj(undefined);
        const fetchPieceJointe = async () => {
            if (id_pj) {
                try {
                    const response = await api.get<Blob>('/fichiers/afficher/' + id_pj, {
                        responseType: 'blob',
                        timeout: 30000,
                    });
                    if (isMounted) {
                        setPj(response.data);
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement de la pièce jointe', error);
                    if (isMounted) {
                        setPj(undefined);
                    }
                }
            }
        };
        fetchPieceJointe();
        return () => {
            isMounted = false;
        };
    }, [id_pj]);
    //merge le pdf s'il y a lieu
    useEffect(() => {
        let isMounted = true; // Pour contrôler l'état après le démontage
        setLoading(true);
        if (blob && (!id_pj || pj)) {
            if (pj) {
                //on doit merge
                merge([blob, pj]).then((result) => {
                    if (isMounted) {
                        setData(result);
                        setLoading(false);
                    }
                });
            } else {
                if (isMounted) {
                    setData(blob);
                    setLoading(false);
                }
            }
        }
        return () => {
            isMounted = false;
        };
    }, [blob, id_pj, pj]);

    return {
        loading,
        data,
        url: data ? window.URL.createObjectURL(data) : undefined,
        name: `Guide d'activité - ${activite?.name}.pdf`,
    };
}

export default function LoadPDF({
    activite,
    className,
    titre,
    chargement,
}: {
    activite: Activite;
    className?: string;
    titre?: ReactNode;
    chargement?: ReactNode;
}): ReactElement {
    const [masquerCovid19, setMasquerCovid19] = useRecoilState(etatMasquerCovid19);
    const { loading, url, name } = useLoadPdf(activite, masquerCovid19.estMasquer);

    return (
        <a className={'btn ' + (loading ? 'disabled' : '') + ` ${className}`} href={url} download={name}>
            <span className="d-flex align-items-center mx-auto">
                {loading ? (
                    <>{chargement || 'Chargement...'}</>
                ) : (
                    <>
                        {titre || (
                            <>
                                <BiFile /> Télécharger le guide
                            </>
                        )}
                    </>
                )}
            </span>
        </a>
    );
}
